import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { NAV_ITEMS } from "../app/routes.tsx";
import { useTimeRange } from "../state/time-range.tsx";

/**
 * The command palette (design 12.2), invoked with Cmd/Ctrl-K from anywhere.
 *
 * In the shell it offers navigation to every screen and the live/pause action; later tasks register
 * screen-specific commands. It is a modal dialog that is fully keyboard operable (design 12.4,
 * NFR-7 AC3): arrow keys move the selection, Enter runs it, Escape closes, focus moves to the input
 * on open and returns to the invoker on close, and the active option is announced through
 * `aria-activedescendant`.
 *
 * Positioning: the panel is anchored directly under the topbar search box (`.topbar__search`) so it
 * reads as a dropdown from the search bar rather than a free-floating centered modal. The anchor is
 * measured from the live search element on open and kept in sync on resize/scroll. If the search box
 * is not present (e.g. a very narrow viewport that hides it), the panel falls back to a centered
 * position near the top of the viewport.
 */

interface Command {
  readonly id: string;
  readonly label: string;
  /** Extra words to match against (e.g. a route path), not shown. */
  readonly keywords: string;
  readonly hint?: string;
  run(): void;
}

interface Anchor {
  readonly left: number;
  readonly top: number;
  readonly width: number;
}

function matches(command: Command, query: string): boolean {
  if (query.length === 0) return true;
  const haystack = `${command.label} ${command.keywords}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .every((term) => haystack.includes(term));
}

/**
 * Measure where the palette should sit: flush under the topbar search box, matching its left edge
 * and width (clamped to a sensible minimum so the list stays readable). Returns null when there is
 * no search box to anchor to, so the caller can fall back to a centered position.
 */
function measureAnchor(): Anchor | null {
  const el = document.querySelector(".topbar__search");
  if (!(el instanceof HTMLElement)) return null;
  const rect = el.getBoundingClientRect();
  const gap = 6;
  // Align to the search box left edge; match its width but clamp to a readable range so a very wide
  // search bar does not produce an unwieldy full-width dropdown.
  const width = Math.min(Math.max(rect.width, 320), 640);
  // Keep the panel within the viewport if the search box sits near the right edge.
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  return {
    left: Math.min(rect.left, maxLeft),
    top: rect.bottom + gap,
    width,
  };
}

export function CommandPalette(): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previouslyFocused = useRef<Element | null>(null);
  const listId = useId();
  // Where to anchor the panel (under the search box); null => fall back to a centered position.
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  const navigate = useNavigate();
  const { toggleLive, live } = useTimeRange();

  const commands = useMemo<Command[]>(() => {
    const nav: Command[] = NAV_ITEMS.map((item) => ({
      id: `nav:${item.path}`,
      label: `Go to ${item.label}`,
      keywords: `${item.path} ${item.label}`,
      hint: item.path,
      run: () => navigate(item.path),
    }));
    const actions: Command[] = [
      {
        id: "action:toggle-live",
        label: live ? "Pause live updates" : "Resume live updates",
        keywords: "live pause resume freeze",
        run: () => toggleLive(),
      },
    ];
    return [...nav, ...actions];
  }, [navigate, toggleLive, live]);

  const results = useMemo(() => commands.filter((c) => matches(c, query)), [commands, query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setSelected(0);
    // Return focus to whatever was focused when the palette opened.
    if (previouslyFocused.current instanceof HTMLElement) previouslyFocused.current.focus();
  }, []);

  // Global Cmd/Ctrl-K to open; the handler lives for the life of the app.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        previouslyFocused.current = document.activeElement;
        setOpen((wasOpen) => !wasOpen);
      }
    };
    // The topbar search affordance opens the palette without a keypress (a `oa:open-palette` event),
    // so the search box and Cmd/Ctrl-K share one palette.
    const onOpen = (): void => {
      previouslyFocused.current = document.activeElement;
      setOpen(true);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("oa:open-palette", onOpen);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("oa:open-palette", onOpen);
    };
  }, []);

  // Focus the input and reset the selection whenever the palette opens.
  useEffect(() => {
    if (open) {
      setSelected(0);
      inputRef.current?.focus();
    }
  }, [open]);

  // Anchor the panel under the search box while open, and keep it in sync as the layout shifts.
  useLayoutEffect(() => {
    if (!open) return;
    const sync = (): void => setAnchor(measureAnchor());
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("scroll", sync, true);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("scroll", sync, true);
    };
  }, [open]);

  // Keep the selection within the (filtered) results.
  useEffect(() => {
    setSelected((current) => (results.length === 0 ? 0 : Math.min(current, results.length - 1)));
  }, [results.length]);

  if (!open) return null;

  const run = (command: Command | undefined): void => {
    if (command === undefined) return;
    close();
    command.run();
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case "Tab":
        // The input is the only focusable control in the modal; keep focus trapped on it so Tab
        // never reaches the inert page behind the palette (design 12.4). Enter/arrows drive the
        // listbox via aria-activedescendant, so the options need not be tab stops.
        event.preventDefault();
        break;
      case "ArrowDown":
        event.preventDefault();
        setSelected((current) => (results.length === 0 ? 0 : (current + 1) % results.length));
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelected((current) =>
          results.length === 0 ? 0 : (current - 1 + results.length) % results.length,
        );
        break;
      case "Enter":
        event.preventDefault();
        run(results[selected]);
        break;
      case "Escape":
        event.preventDefault();
        close();
        break;
      default:
        break;
    }
  };

  const activeId = results[selected] === undefined ? undefined : `${listId}-${results[selected].id}`;

  // When anchored, drop the panel under the search box; otherwise centre it near the top (fallback).
  const panelStyle: React.CSSProperties =
    anchor === null
      ? {}
      : { position: "absolute", left: anchor.left, top: anchor.top, width: anchor.width };

  return (
    <div
      className={anchor === null ? "palette-backdrop" : "palette-backdrop palette-backdrop--anchored"}
      role="presentation"
      onMouseDown={(event) => {
        // A click on the backdrop (not the panel) closes.
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        style={panelStyle}
      >
        <input
          ref={inputRef}
          className="palette__input"
          type="text"
          placeholder="Type a command or screen…"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          {...(activeId === undefined ? {} : { "aria-activedescendant": activeId })}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onInputKeyDown}
        />
        {results.length === 0 ? (
          <div className="palette__empty">No matching commands.</div>
        ) : (
          <ul className="palette__list" id={listId} role="listbox">
            {results.map((command, index) => (
              <li
                key={command.id}
                id={`${listId}-${command.id}`}
                className="palette__item"
                role="option"
                aria-selected={index === selected}
                onMouseEnter={() => setSelected(index)}
                onMouseDown={(event) => {
                  // Prevent the input blur before the click runs.
                  event.preventDefault();
                  run(command);
                }}
              >
                <span>{command.label}</span>
                {command.hint !== undefined && (
                  <span style={{ color: "var(--text-faint)" }}>{command.hint}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
